import { CURRENCY_CODES } from '@watershed/constants/currency';

/**
 * # User created schema units
 *
 * This is a curated set of units that we've deemed appropriate for user-created
 * schemas. (For example, in Custom Activity Types and Calc Customization's
 * Reference Data.)
 *
 * We need to specify all allowed aliases, as well as the canonical units. It's
 * possible for the set to allow an alias but not the canonical unit! That
 * allows us to use this set in UI interfaces like autocompletes.
 *
 * 🚨 🚨 🚨 **If you are wondering why a unit is not available in user-created
 * schema interfaces, it's because that unit is not in this set!**
 *
 * Here are the basic rules about inclusion in the set (except in rare cases
 * that have inline comments below):
 *
 * - All units are singular snake_case (non-singular aliases are excluded, like
 *   'miles' for 'mile'; and non-lowercase aliases are excluded, like 'MJ' for
 *   'megajoule').
 * - No compound units (mostly). Compound units are created by composing these.
 *   It would be confusing to have a list of "valid units" that includes some
 *   compound units and not others.
 * - We don't include units that we don't have strong reason to believe users
 *   will need to use in our particular business domain (e.g. 'planck_length',
 *   'astronomical_unit', 'pica', 'hogshead'). One way that we've made this
 *   judgment call: on 2025-10-17, we surveyed our usage in BATs and reference
 *   data and (mostly) excluded units that were never used in those contexts.
 *
 * ⛔️ We have erred on the side of *exclusion* here, including units that we
 * know are used (by BATs or reference data) or very likely to be used based on
 * other usage patterns.
 *
 * ## Tests
 *
 * 🧪 The Jest tests `userCreatedSchemaUnits.test.ts` ensure that all the
 * strings below really are units (TypeScript can't do that).
 *
 * ## Adding new units
 *
 * - Make sure the unit exists in the main library.
 * - Add the unit to the set below.
 * - Run the tests to update the snapshot.
 *
 * ## Removing units
 *
 * 👎 You really should not, unless you have somehow verified that nobody is
 * using it in any system that relies on this list.
 */
export const userCreatedSchemaUnits: ReadonlySet<string> = new Set([
  // ================================
  // Area
  // ================================

  // Units
  'square_meter',
  'square_foot',
  'hectare',
  'acre',

  // Aliases
  'sqft',

  // ================================
  // Calendar
  // ================================

  // Units
  'month',
  'year',

  // ================================
  // Carbon
  // ================================

  // Units
  'kgco2e',
  'tonneco2e',
  'kilotonneco2e',
  'megatonneco2e',
  'gigatonneco2e',

  // ================================
  // Currency
  // ================================

  // EXCEPTION! Currency codes follow ISO 4217 standard and are conventionally uppercase.
  ...CURRENCY_CODES,

  // ================================
  // Energy
  // ================================

  // Units
  'joule',
  'kilojoule',
  'megajoule',
  'gigajoule',
  'terajoule',
  'british_thermal_unit',
  'mmbtu',
  'kbtu',
  'therm',

  // Aliases
  'btu',
  // EXCEPTION! This is a common and useful abbrevation in compound units.
  'kwh',
  // EXCEPTION! This is a common and useful abbrevation in compound units.
  'mwh',
  // EXCEPTION! This is a common and useful abbrevation in compound units.
  'wh',

  // ================================
  // Fuel Economy
  // ================================

  // EXCEPTION! This compound unit is a very typical unit for fuel economy. But
  // it cannot be expressed as a regular compound (`liter/100km`) *unless* we
  // also expose `100km` as a simple unit, which (1) would be confusing, because
  // that's not really a unit; and (2) would be complex, because we would want
  // to block people from using it *outside of* this particular compound.
  'liter_per_100km',

  // Aliases
  // EXCEPTION! This is a common and useful abbrevation in compound units.
  'mpg',

  // ================================
  // Information
  // ================================

  // Units
  'bit',
  'kilobit',
  'megabit',
  'gigabit',
  'terabit',
  'petabit',
  'exabit',
  'kibibit',
  'mebibit',
  'gibibit',
  'tebibit',
  'pebibit',
  'byte',
  'kilobyte',
  'megabyte',
  'gigabyte',
  'terabyte',
  'petabyte',
  'exabyte',
  'kibibyte',
  'mebibyte',
  'gibibyte',
  'tebibyte',
  'pebibyte',

  // ================================
  // Length
  // ================================

  // Units
  'meter',
  'kilometer',
  'mile',
  'nautical_mile',

  // Aliases
  'km',
  'm',

  // ================================
  // Mass
  // ================================

  // Units
  'kilogram',
  'gram',
  'milligram',
  'tonne',
  'kilotonne',
  'megatonne',
  'gigatonne',
  'ounce',
  'pound',
  'short_ton',

  // Aliases
  'kg',
  'g',
  'lb',
  'mg',
  'ton',

  // ================================
  // Power
  // ================================

  // Units
  'watt',
  'milliwatt',
  'kilowatt',
  'megawatt',
  'gigawatt',
  'terawatt',

  // Aliases
  'kw',

  // ================================
  // Temperature
  // ================================

  // Units
  'celsius',
  'fahrenheit',
  'kelvin',

  // ================================
  // Textile
  // ================================

  // Units
  'tex',
  'denier',
  'decitex',
  'number_metric',
  'number_english',

  // Aliases
  'dtex',

  // ================================
  // Time
  // ================================

  // Units
  'second',
  'millisecond',
  'microsecond',
  'nanosecond',
  'minute',
  'hour',
  'day',
  'week',

  // Aliases
  's',

  // ================================
  // Volume
  // ================================

  // Units
  'liter',
  'milliliter',
  'kiloliter',
  '9le',
  'cubic_meter',
  'cubic_foot',
  'c_cubic_foot',
  'm_cubic_foot',
  'cubic_yard',
  'gallon',
  'kilogallon',
  'centigallon',
  'fluid_ounce',

  // Aliases
  'l',
  'ml',
  'cc',
  'cl',
  'cgal',
  'oz',
  'scf',
  'cf',
  'mcf',
  'ccf',
]);
